import { BaseGrid } from '@/grid/BaseGrid';
import { Cell } from '@/grid/Cell';
import { AStar } from '../algorithms/AStar';
import { Heuristic } from '../core/Heuristic';
import { AlgorithmStep } from '../core/PathResult';
import { AbstractNode } from './AbstractGraph';
import { IntraClusterCache } from './IntraClusterCache';

export class ClusterPathRefinement {
  static *refineAbstractPath(
    abstractPath: AbstractNode[],
    grid: BaseGrid,
    cache: IntraClusterCache
  ): Generator<
    AlgorithmStep,
    { path: Cell[]; localNodesExplored: number; cacheHits: number },
    undefined
  > {
    const refinedPath: Cell[] = [];
    let localNodesExplored = 0;
    let cacheHits = 0;
    const aStar = new AStar();

    for (let i = 0; i < abstractPath.length - 1; i++) {
      const currentNode = abstractPath[i];
      const nextNode = abstractPath[i + 1];

      const cluster = currentNode.entrance.cluster1;
      const startCell =
        currentNode.position.row === currentNode.entrance.cell1.row &&
        currentNode.position.col === currentNode.entrance.cell1.col
          ? currentNode.entrance.cell1
          : currentNode.entrance.cell2;

      const goalCell =
        nextNode.position.row === nextNode.entrance.cell1.row &&
        nextNode.position.col === nextNode.entrance.cell1.col
          ? nextNode.entrance.cell1
          : nextNode.entrance.cell2;

      const cacheEntry = cache.get(cluster, startCell, goalCell);

      if (cacheEntry) {
        cacheHits++;
        const cachedPath = cacheEntry.path;

        if (refinedPath.length > 0) {
          refinedPath.pop();
        }
        refinedPath.push(...cachedPath);

        yield {
          type: 'path_backtrack',
          cell: startCell,
          openListSize: 0,
          closedListSize: 0,
          metadata: { phase: 'refinement', action: 'cache_hit', clusterId: cluster.id }
        };
      } else {
        const generator = aStar.findPathStepByStep(grid, startCell, goalCell, {
          heuristic: Heuristic.manhattan
        });

        let step = generator.next();
        while (!step.done) {
          const yieldStep = step.value;
          yieldStep.metadata = {
            phase: 'refinement',
            action: 'local_search',
            clusterId: cluster.id
          };
          yield yieldStep;
          step = generator.next();
        }

        const result = step.value;
        localNodesExplored += result.nodesExplored;

        if (result.success) {
          const segmentPath = result.path;
          cache.set(cluster, startCell, goalCell, segmentPath, result.path.length);

          if (refinedPath.length > 0) {
            refinedPath.pop();
          }
          refinedPath.push(...segmentPath);
        }
      }
    }

    return { path: refinedPath, localNodesExplored, cacheHits };
  }
}
